import React, { useMemo } from 'react'
import { Artifact } from '@trustero/trustero-api-web/lib/artifact/artifact_pb'
import { getEvidenceCsv } from 'src/Utils/Evidence/evidence.helpers'
import { convertRowSecondsToISODate } from 'src/Utils/grid/grid.helpers'
import { useSearchableEvidenceInstance } from 'src/pages/Evidence/evidence.hooks'
import { useApplySearch } from 'src/Utils/search/search.hooks'
import { Markup } from 'src/components/Reusable/Text/Markup'
import { CloseButton } from 'src/components/Reusable/Document/DocumentViewer.components'
import { useToggleDocumentViewer } from 'src/components/Reusable/Document/DocumentViewer.hooks'
import { CsvDataGridContainer } from 'src/components/ModalForms/Evidence/evidence.styles'
import {
  getArtifactCsv,
  getTestTableHeaderTooltip,
  getHighlightRowClassName,
} from 'src/pages/TrusteroIntelligence/artifacts/Artifacts.helpers'
import {
  AutomaticEvidenceContainer,
  EvidenceShowTabBody,
  StyledCsvBody,
  StyledCsvModal,
} from '../../../../evidence.styles'
import {
  EvidenceDataGrid,
  AutomaticEvidenceToolbar,
} from '../AutomaticEvidence/AutomaticEvidenceBody'

export const ArtifactEvidenceBody = ({
  body,
}: {
  body: Uint8Array
}): JSX.Element => {
  const { isModalOpen, toggleModal } = useToggleDocumentViewer()

  const { rows, columns, noData, getRowClassName } = useMemo(() => {
    try {
      const artifact = Artifact.deserializeBinary(body)
      const csv = getArtifactCsv(artifact)
      if (!csv) {
        return {
          rows: [],
          columns: [],
          noData: true,
          getRowClassName: undefined,
        }
      }
      const headerTooltip = getTestTableHeaderTooltip(artifact)
      const { rows: parsedRows, columns } = getEvidenceCsv(csv, headerTooltip)
      const rows = parsedRows.map((row) => convertRowSecondsToISODate(row))
      const getRowClassName = getHighlightRowClassName(artifact)
      return { rows, columns, noData: false, getRowClassName }
    } catch {
      return {
        rows: [],
        columns: [],
        noData: true,
        getRowClassName: undefined,
      }
    }
  }, [body])

  const searchableRows = useSearchableEvidenceInstance([...rows])
  const { data: matchingRows } = useApplySearch(searchableRows, 'evidence')

  if (!body) return <></>

  if (noData) {
    return (
      <EvidenceShowTabBody>
        <Markup markdown="No data available for this artifact." />
      </EvidenceShowTabBody>
    )
  }

  return (
    <AutomaticEvidenceContainer>
      <AutomaticEvidenceToolbar toggleModal={toggleModal} />
      <CsvDataGridContainer>
        <EvidenceDataGrid
          rows={matchingRows}
          columns={columns}
          getRowClassName={getRowClassName}
        />
      </CsvDataGridContainer>
      {isModalOpen && (
        <StyledCsvModal>
          <StyledCsvBody>
            <CsvDataGridContainer>
              <EvidenceDataGrid
                rows={matchingRows}
                columns={columns}
                getRowClassName={getRowClassName}
              />
            </CsvDataGridContainer>
          </StyledCsvBody>
          <CloseButton onClick={toggleModal} />
        </StyledCsvModal>
      )}
    </AutomaticEvidenceContainer>
  )
}
